// import { navigateToUrl as originalNavigateToUrl } from 'single-spa'

type originalNavigateToUrl = (
  obj:
    | string
    | {
        currentTarget: {
          href: string
        }
        preventDefault: any
      },
  opts?: Record<string, any>
) => void

/**
 * 所有方法均来自 https://github.com/CanopyTax/single-spa/blob/master/src/navigation/navigation-events.js
 */

function isSamePath(destination: string, current: string) {
  // if the destination has a path but no domain, it doesn't include the root '/'
  return current === destination || current === '/' + destination
}

function parseUri(str: string) {
  // parseUri 1.2.2
  // (c) Steven Levithan <stevenlevithan.com>
  // MIT License
  // http://blog.stevenlevithan.com/archives/parseuri

  const parseOptions = <const>{
    strictMode: true,
    key: [
      'source',
      'protocol',
      'authority',
      'userInfo',
      'user',
      'password',
      'host',
      'port',
      'relative',
      'path',
      'directory',
      'file',
      'query',
      'anchor',
    ],
    q: {
      name: 'queryKey',
      parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
    },
    parser: {
      strict: /^(?:([^:/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?))?((((?:[^?#/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
      loose: /^(?:(?![^:@]+:[^:@/]*@)([^:/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#/]*\.[^?#/.]+(?:[?#]|$)))*\/?)?([^?#/]*))(?:\?([^#]*))?(?:#(.*))?)/,
    },
  }

  let o = parseOptions
  let m = o.parser[o.strictMode ? 'strict' : 'loose'].exec(str)

  // type Uri<T> = T extends typeof parseOptions["key"][number]
  //   ? {
  //       [key in T]?: string
  //     }
  //   : T extends typeof parseOptions["key"][number]
  //   ? {
  //       [key in T]?: Record<string, string>
  //     }
  //   : never
  type PathKeys = typeof parseOptions['key'][number]
  type QueryKey = typeof parseOptions['q']['name']

  type Uri<T extends string> = {
    [key in T]?: key extends PathKeys
      ? string
      : key extends QueryKey
      ? Record<string, any>
      : never
  }

  let uri: Uri<PathKeys | QueryKey> = {}
  let i = 14

  while (i--) {
    uri[o.key[i]] = m![i] || ''
  }

  uri[o.q.name] = {}
  uri[o.key[12]]!.replace(o.q.parser, function ($0: string, $1: any, $2: any) {
    if ($1) {
      uri[o.q.name]![$1] = $2
    }
    return $0
  })

  return uri
}

export function navigateToUrl(
  this: void | HTMLLinkElement,
  ...args: Parameters<originalNavigateToUrl>
): {
  url: string
  hasJump: boolean
  wouldHaveReloadedThePage?: boolean
} {
  const [obj, opts = {}] = args

  /** 获取要跳转的 URL */
  let url
  if (typeof obj === 'string') {
    url = obj
  } else if (this && this.href) {
    url = this.href
  } else if (
    obj &&
    obj.currentTarget &&
    obj.currentTarget.href &&
    obj.preventDefault
  ) {
    url = obj.currentTarget.href
    obj.preventDefault()
  } else {
    throw Error(
      `singleSpaNavigate must be either called with a string url, with an <a> tag as its context, or with an event whose currentTarget is an <a> tag`
    )
  }

  const current = parseUri(window.location.href)
  const destination = parseUri(url)

  if (url.indexOf('#') === 0) {
    window.location.hash = '#' + destination.anchor
    return {
      url,
      hasJump: true,
    }
  } else if (current.host !== destination.host && destination.host) {
    if ((opts as Record<string, any>).isTestingEnv) {
      return {
        url,
        hasJump: false,
        wouldHaveReloadedThePage: true,
      }
    } else {
      window.location.href = url
      return {
        url,
        hasJump: true,
      }
    }
  } else if (
    !isSamePath(
      destination.path + '?' + destination.query,
      current.path + '?' + current.query
    )
  ) {
    // different path, host, or query params
    window.history.pushState(null, '', url)
    return {
      url,
      hasJump: true,
    }
  } else if (destination.anchor !== current.anchor) {
    window.location.hash = '#' + destination.anchor
    return {
      url,
      hasJump: true,
    }
  }
  return {
    url,
    hasJump: false,
  }
}
